import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, MapPin, Package, Pencil, Plus, Store, Trash2 } from 'lucide-react';
import authFetch from '../../utils/authFetch';
import { PropertyBadge, PropertyFilter, PropertySelect, strBasePath, strPropertyQuery, useStrProperty } from './properties';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';
const VAT_RATE = 0.22;

const USAGE_LABELS = { stay: 'per stay', day: 'per rental day' };
// A per-day item is estimated as if every stay lasted this long.
const ESTIMATE_DAYS_PER_STAY = 3;

const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (iso) => {
  const [year, month, day] = String(iso).split('-');
  return `${Number(day)}.${Number(month)}.${year}`;
};
const formatMoney = (value) => `€${Number(value).toFixed(2)}`;
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// How many more stays the stock covers; null when the item has no usage set.
const staysLeft = (item) => {
  if (!item.usage_amount) return null;
  const perStay = item.usage_basis === 'day' ? item.usage_amount * ESTIMATE_DAYS_PER_STAY : item.usage_amount;
  // Rounded first so that e.g. 0.3 / 0.1 does not come out as 2.
  return Math.floor(Math.round((item.quantity / perStay) * 1000) / 1000);
};

const byListOrder = (a, b) => (Number(a.not_in_use) - Number(b.not_in_use)) || a.name.toLowerCase().localeCompare(b.name.toLowerCase());

const describeFinancial = (entry) => {
  if (!entry.expense_id) return null;
  if (entry.expense_type !== 'company') return 'In financials: private';
  return `In financials: company, ${entry.amount_with_vat === null ? 'without VAT' : 'with VAT'}`;
};

const readError = async (response, fallback) => {
  const data = await response.json().catch(() => ({}));
  return data.error || fallback;
};

const InventoryPage = () => {
  const navigate = useNavigate();
  // Set on a property's own page; null on the combined page, which adds the filter and property badges.
  const property = useStrProperty();
  const fixedKey = property?.key;

  const [items, setItems] = useState([]);
  const [canAddToFinancials, setCanAddToFinancials] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    authFetch(`${API_BASE}/api/short-term-rentals/inventory${strPropertyQuery(fixedKey)}`)
      .then(async (response) => {
        if (!response.ok) throw new Error(await readError(response, 'Failed to load the inventory'));
        const data = await response.json();
        if (cancelled) return;
        setItems(data.items || []);
        setCanAddToFinancials(Boolean(data.canAddToFinancials));
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fixedKey]);

  const replaceItem = (item) => setItems((prev) => prev.map((other) => (other.id === item.id ? item : other)));

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      if (!fixedKey && propertyFilter !== 'all' && item.property_key !== propertyFilter) return false;
      if (!term) return true;
      return [item.name, item.location, item.description, item.supplier].some((field) => (field || '').toLowerCase().includes(term));
    }).sort(byListOrder);
  }, [items, fixedKey, propertyFilter, search]);

  const createItem = async (values) => {
    const response = await authFetch(`${API_BASE}/api/short-term-rentals/inventory`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...values, propertyKey: fixedKey || values.propertyKey })
    });
    if (!response.ok) throw new Error(await readError(response, 'Failed to add the item'));
    const data = await response.json();
    setItems((prev) => [...prev, data.item]);
    setShowNewForm(false);
  };

  const removeItem = async (item) => {
    const inFinancials = item.stock.some((entry) => entry.expense_id);
    const note = inFinancials ? ' Its entries in Financials stay there.' : '';
    if (!window.confirm(`Delete "${item.name}" and its stock history?${note}`)) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/inventory/${item.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await readError(response, 'Failed to delete the item'));
      setItems((prev) => prev.filter((other) => other.id !== item.id));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      <button onClick={() => navigate(strBasePath(property))} style={backButtonStyle}>
        <ArrowLeft size={16} /> {property ? `Back to ${property.name}` : 'Back to Short Term Rentals'}
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
        <div>
          <h1 style={{ color: '#1e293b', margin: '0 0 6px' }}>Inventory</h1>
          <p style={{ color: '#64748b', margin: 0 }}>
            {property ? 'Consumables at this property, where they are and how many stays the stock covers.' : 'Items of all properties in one list, with a filter for each one.'}
          </p>
        </div>
        {!showNewForm && (
          <button onClick={() => setShowNewForm(true)} style={primaryButtonStyle}><Plus size={16} /> Add item</button>
        )}
      </div>

      {error && <p style={{ color: '#dc2626', marginBottom: '16px' }}>{error}</p>}

      {showNewForm && (
        <ItemForm needsProperty={!fixedKey} onSave={createItem} onCancel={() => setShowNewForm(false)} />
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '12px', marginBottom: '16px' }}>
        {!fixedKey && <PropertyFilter value={propertyFilter} onChange={setPropertyFilter} />}
        <label style={{ ...labelStyle, flex: '1 1 220px', marginBottom: 0 }}>
          Search
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, location, shop..." style={inputStyle} />
        </label>
      </div>

      {loading ? (
        <p style={{ color: '#64748b' }}>Loading...</p>
      ) : visibleItems.length === 0 ? (
        <div style={cardStyle}>
          <p style={{ color: '#64748b', margin: 0 }}>{items.length === 0 ? 'No items yet. Use "Add item" to start.' : 'No items match.'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {visibleItems.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              showProperty={!fixedKey}
              canAddToFinancials={canAddToFinancials}
              onChanged={replaceItem}
              onDelete={() => removeItem(item)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ItemForm = ({ initial, needsProperty, onSave, onCancel }) => {
  const [values, setValues] = useState({
    propertyKey: '',
    name: initial?.name || '',
    notInUse: Boolean(initial?.not_in_use),
    location: initial?.location || '',
    description: initial?.description || '',
    quantity: initial ? String(initial.quantity) : '0',
    usageAmount: initial?.usage_amount ? String(initial.usage_amount) : '',
    usageBasis: initial?.usage_basis || 'stay',
    supplier: initial?.supplier || '',
    price: initial?.price !== null && initial?.price !== undefined ? String(initial.price) : ''
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const set = (field) => (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (needsProperty && !values.propertyKey) {
      setFormError('Select a property for this item.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await onSave(values);
    } catch (err) {
      setFormError(err.message);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ ...cardStyle, marginBottom: '20px' }}>
      <h3 style={{ margin: '0 0 14px', color: '#1e293b', fontSize: '16px' }}>{initial ? `Edit ${initial.name}` : 'New item'}</h3>
      {needsProperty && (
        <div style={{ ...labelStyle, marginBottom: '12px' }}>
          Property
          <div style={{ marginTop: '5px' }}>
            <PropertySelect value={values.propertyKey} onChange={(key) => setValues((prev) => ({ ...prev, propertyKey: key }))} />
          </div>
        </div>
      )}
      <div style={rowStyle}>
        <label style={{ ...labelStyle, flex: '1 1 220px' }}>
          Name
          <input type="text" value={values.name} onChange={set('name')} required maxLength={255} style={inputStyle} autoFocus={!initial} />
        </label>
      </div>
      <div style={rowStyle}>
        <label style={{ ...labelStyle, flex: '2 1 220px' }}>
          Location
          <input type="text" value={values.location} onChange={set('location')} maxLength={255} placeholder="e.g. Kitchen cupboard" style={inputStyle} />
        </label>
        <label style={{ ...labelStyle, flex: '1 1 120px' }}>
          In stock
          <input type="number" min="0" step="any" value={values.quantity} onChange={set('quantity')} style={inputStyle} />
        </label>
      </div>
      <div style={rowStyle}>
        <label style={{ ...labelStyle, flex: '1 1 160px' }}>
          Used (optional)
          <input type="number" min="0" step="any" value={values.usageAmount} onChange={set('usageAmount')} placeholder="How many" style={inputStyle} />
        </label>
        <label style={{ ...labelStyle, flex: '1 1 160px' }}>
          Per
          <select value={values.usageBasis} onChange={set('usageBasis')} disabled={!values.usageAmount} style={inputStyle}>
            <option value="stay">Stay</option>
            <option value="day">Rental day</option>
          </select>
        </label>
      </div>
      <div style={rowStyle}>
        <label style={{ ...labelStyle, flex: '2 1 220px' }}>
          Where it was bought
          <input type="text" value={values.supplier} onChange={set('supplier')} maxLength={255} style={inputStyle} />
        </label>
        <label style={{ ...labelStyle, flex: '1 1 120px' }}>
          Price (€)
          <input type="number" min="0" step="0.01" value={values.price} onChange={set('price')} placeholder="0.00" style={inputStyle} />
        </label>
      </div>
      <label style={labelStyle}>
        Description
        <textarea value={values.description} onChange={set('description')} maxLength={2000} rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontSize: '14px', fontWeight: 600, marginTop: '12px', cursor: 'pointer' }}>
        <input type="checkbox" checked={values.notInUse} onChange={(e) => setValues((prev) => ({ ...prev, notInUse: e.target.checked }))} />
        Not in use
      </label>
      {formError && <p style={{ color: '#dc2626', fontSize: '14px', margin: '10px 0 0' }}>{formError}</p>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
        <button type="submit" disabled={saving} style={primaryButtonStyle}>{saving ? 'Saving...' : initial ? 'Save' : 'Add item'}</button>
        <button type="button" onClick={onCancel} style={plainButtonStyle}>Cancel</button>
      </div>
    </form>
  );
};

const StockForm = ({ item, canAddToFinancials, onSaved, onCancel }) => {
  const [quantity, setQuantity] = useState('');
  const [cost, setCost] = useState('');
  const [date, setDate] = useState(localToday());
  const [supplier, setSupplier] = useState(item.supplier || '');
  const [addToFinancials, setAddToFinancials] = useState(false);
  const [expenseType, setExpenseType] = useState('private');
  const [vatMode, setVatMode] = useState('withVat');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const costNumber = Number.parseFloat(cost);
  const hasCost = Number.isFinite(costNumber) && costNumber > 0;
  const defaultDescription = `${item.name}${quantity ? ` x ${quantity}` : ''} (inventory)`;

  const submit = async (e) => {
    e.preventDefault();
    if (addToFinancials && !hasCost) {
      setFormError('Enter the cost to add this to financials.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/inventory/${item.id}/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quantity,
          cost,
          date,
          supplier,
          addToFinancials,
          financials: addToFinancials ? { expenseType, vatMode, description: description.trim() || defaultDescription } : undefined
        })
      });
      if (!response.ok) throw new Error(await readError(response, 'Failed to add the stock'));
      const data = await response.json();
      onSaved(data.item);
    } catch (err) {
      setFormError(err.message);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ marginTop: '14px', padding: '14px', borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
      <h4 style={{ margin: '0 0 12px', color: '#1e293b', fontSize: '14px' }}>Add stock</h4>
      <div style={rowStyle}>
        <label style={{ ...labelStyle, flex: '1 1 110px' }}>
          How many
          <input type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} required style={inputStyle} autoFocus />
        </label>
        <label style={{ ...labelStyle, flex: '1 1 110px' }}>
          Total cost (€)
          <input type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" style={inputStyle} />
        </label>
        <label style={{ ...labelStyle, flex: '1 1 150px' }}>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required style={inputStyle} />
        </label>
      </div>
      <label style={labelStyle}>
        Where it was bought
        <input type="text" value={supplier} onChange={(e) => setSupplier(e.target.value)} maxLength={255} style={inputStyle} />
      </label>

      {canAddToFinancials && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontSize: '14px', fontWeight: 600, marginTop: '12px', cursor: 'pointer' }}>
          <input type="checkbox" checked={addToFinancials} onChange={(e) => setAddToFinancials(e.target.checked)} />
          Add to financials
        </label>
      )}

      {addToFinancials && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button type="button" onClick={() => setExpenseType('private')} aria-pressed={expenseType === 'private'} style={expenseType === 'private' ? toggleActiveStyle : toggleStyle}>Private</button>
            <button type="button" onClick={() => setExpenseType('company')} aria-pressed={expenseType === 'company'} style={expenseType === 'company' ? toggleActiveStyle : toggleStyle}>Company</button>
          </div>
          {expenseType === 'company' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', marginBottom: '12px' }}>
              <label style={radioStyle}>
                <input type="radio" checked={vatMode === 'withVat'} onChange={() => setVatMode('withVat')} />
                Cost is with VAT (22%)
              </label>
              <label style={radioStyle}>
                <input type="radio" checked={vatMode === 'withoutVat'} onChange={() => setVatMode('withoutVat')} />
                Cost is without VAT (no VAT applicable)
              </label>
            </div>
          )}
          <label style={labelStyle}>
            Description in financials
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={defaultDescription} maxLength={500} style={inputStyle} />
          </label>
          {expenseType === 'company' && vatMode === 'withVat' && hasCost && (
            <p style={{ color: '#475569', fontSize: '14px', margin: '8px 0 0' }}>Without VAT: <strong>{formatMoney(costNumber / (1 + VAT_RATE))}</strong></p>
          )}
        </div>
      )}

      {formError && <p style={{ color: '#dc2626', fontSize: '14px', margin: '10px 0 0' }}>{formError}</p>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
        <button type="submit" disabled={saving} style={primaryButtonStyle}>{saving ? 'Saving...' : 'Save stock'}</button>
        <button type="button" onClick={onCancel} style={plainButtonStyle}>Cancel</button>
      </div>
    </form>
  );
};

const ItemCard = ({ item, showProperty, canAddToFinancials, onChanged, onDelete }) => {
  const [panel, setPanel] = useState(null); // null | 'edit' | 'stock'
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState('');
  const stays = staysLeft(item);
  const history = [
    ...item.stock.map((entry) => ({ kind: 'stock', date: entry.stock_date, entry })),
    ...(item.stayUsage || []).map((entry) => ({ kind: 'stay', date: entry.check_out, entry }))
  ].sort((a, b) => b.date.localeCompare(a.date));

  const saveEdit = async (values) => {
    const response = await authFetch(`${API_BASE}/api/short-term-rentals/inventory/${item.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values)
    });
    if (!response.ok) throw new Error(await readError(response, 'Failed to update the item'));
    const data = await response.json();
    onChanged(data.item);
    setPanel(null);
  };

  const removeEntry = async (entry) => {
    const message = entry.expense_id
      ? 'Remove this stock entry? The amount comes off the stock and its entry in Financials is deleted too.'
      : 'Remove this stock entry? The amount comes off the stock.';
    if (!window.confirm(message)) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/inventory/stock/${entry.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await readError(response, 'Failed to remove the stock entry'));
      const data = await response.json();
      onChanged(data.item);
    } catch (err) {
      setError(err.message);
    }
  };

  if (panel === 'edit') {
    return <ItemForm initial={item} onSave={saveEdit} onCancel={() => setPanel(null)} />;
  }

  return (
    <div style={cardStyle} data-testid={`item-${item.name}`}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '16px' }}>{item.name}</h3>
            {item.not_in_use && (
              <span style={{ borderRadius: '999px', padding: '3px 10px', fontSize: '12px', fontWeight: 600, color: '#475569', backgroundColor: '#f1f5f9' }}>Not in use</span>
            )}
            {showProperty && <PropertyBadge propertyKey={item.property_key} />}
          </div>
          {item.description && <p style={{ color: '#475569', fontSize: '14px', margin: '0 0 8px', whiteSpace: 'pre-wrap' }}>{item.description}</p>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', color: '#475569', fontSize: '13px' }}>
            {item.location && <span style={metaStyle}><MapPin size={14} /> {item.location}</span>}
            {item.supplier && <span style={metaStyle}><Store size={14} /> {item.supplier}</span>}
            {item.price !== null && <span>Price {formatMoney(item.price)}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: item.quantity > 0 || item.not_in_use ? '#1e293b' : '#b91c1c' }}>{item.quantity}</div>
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>in stock</div>
        </div>
      </div>

      {item.usage_amount !== null && (
        <p style={{ color: '#475569', fontSize: '13px', margin: '8px 0 0' }}>
          Uses {item.usage_amount} {USAGE_LABELS[item.usage_basis]}
          {!item.not_in_use && (
            <>
              {' · '}
              <strong style={{ color: stays === 0 ? '#b91c1c' : '#1e293b' }}>Stock for {plural(stays, 'more stay', 'more stays')}</strong>
              {item.usage_basis === 'day' && ` (counting ${ESTIMATE_DAYS_PER_STAY} days per stay)`}
            </>
          )}
        </p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
        <button onClick={() => setPanel(panel === 'stock' ? null : 'stock')} style={secondaryButtonStyle}><Package size={14} /> Add stock</button>
        <button onClick={() => setPanel('edit')} style={secondaryButtonStyle}><Pencil size={14} /> Edit</button>
        <button onClick={onDelete} style={dangerButtonStyle}><Trash2 size={14} /> Delete</button>
        {history.length > 0 && (
          <button onClick={() => setShowHistory((prev) => !prev)} style={plainButtonStyle}>
            {showHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Stock history ({history.length})
          </button>
        )}
      </div>

      {error && <p style={{ color: '#dc2626', fontSize: '14px', margin: '10px 0 0' }}>{error}</p>}

      {panel === 'stock' && (
        <StockForm
          key={item.stock.length}
          item={item}
          canAddToFinancials={canAddToFinancials}
          onSaved={(updated) => { onChanged(updated); setPanel(null); setShowHistory(true); }}
          onCancel={() => setPanel(null)}
        />
      )}

      {showHistory && history.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          {history.map(({ kind, entry }) => (kind === 'stay' ? (
            <div key={`stay-${entry.id}`} data-testid="stay-entry" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px', padding: '8px 0', borderTop: '1px solid #f1f5f9', fontSize: '13px', color: '#334155' }}>
              <span style={{ fontWeight: 600 }}>{formatDate(entry.check_out)}</span>
              <span>-{entry.quantity}</span>
              <span style={{ color: '#64748b' }}>Stay {formatDate(entry.check_in)} – {formatDate(entry.check_out)}</span>
            </div>
          ) : (
            <div key={`stock-${entry.id}`} data-testid="stock-entry" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px', padding: '8px 0', borderTop: '1px solid #f1f5f9', fontSize: '13px', color: '#334155' }}>
              <span style={{ fontWeight: 600 }}>{formatDate(entry.stock_date)}</span>
              <span>+{entry.quantity}</span>
              {entry.cost !== null && <span>{formatMoney(entry.cost)}</span>}
              {entry.supplier && <span style={{ color: '#64748b' }}>{entry.supplier}</span>}
              {entry.added_by_name && <span style={{ color: '#94a3b8' }}>by {entry.added_by_name}</span>}
              {entry.expense_id && <span style={{ color: '#047857', fontWeight: 600 }}>{describeFinancial(entry)}</span>}
              <button onClick={() => removeEntry(entry)} style={{ ...dangerButtonStyle, marginLeft: 'auto', padding: '4px 6px' }} aria-label="Remove stock entry" title="Remove stock entry">
                <Trash2 size={12} />
              </button>
            </div>
          )))}
        </div>
      )}
    </div>
  );
};

const backButtonStyle = { display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '14px', marginBottom: '20px', padding: 0 };
const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', boxSizing: 'border-box' };
const rowStyle = { display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' };
const labelStyle = { display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, minWidth: 0 };
const inputStyle = { display: 'block', width: '100%', marginTop: '5px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: 400, boxSizing: 'border-box', backgroundColor: 'white' };
const metaStyle = { display: 'inline-flex', alignItems: 'center', gap: '4px' };
const radioStyle = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#334155', cursor: 'pointer' };
const primaryButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 };
const secondaryButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 };
const dangerButtonStyle = { ...secondaryButtonStyle, backgroundColor: '#fef2f2', color: '#dc2626' };
const plainButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 };
const toggleStyle = { padding: '8px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', cursor: 'pointer', fontWeight: 600 };
const toggleActiveStyle = { ...toggleStyle, backgroundColor: '#1e293b', color: 'white', borderColor: '#1e293b' };

export default InventoryPage;
